'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'
import { format, parseISO } from 'date-fns'
import EventListItem from './EventListItem'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'
import { citySlug } from '@/lib/timed-ui/city-slug'
import type { TimedUIEvent } from '@/lib/timed-ui/adapters'

interface LocationGroup {
  city: string
  venueNames: string[]
  events: TimedUIEvent[]
}

interface DateGroup {
  dateKey: string
  dateLabel: string
  dayOfWeek: string
  events: TimedUIEvent[]
}

interface LocationGroupViewProps {
  events: TimedUIEvent[]
  /**
   * 'expand' (default) — clicking a card toggles inline expansion.
   * 'page'   — clicking a card routes to /locations/[city].
   */
  clickBehavior?: 'expand' | 'page'
  /**
   * Ordered list of city slugs (kebab-case). Cities matching these
   * slugs render first, in this order, with a "Featured" badge.
   * Everything else falls below in alphabetical order.
   */
  featuredSlugs?: string[]
}

/** Sub-group events by calendar date within a location */
function groupByDate(events: TimedUIEvent[]): DateGroup[] {
  const map = new Map<string, TimedUIEvent[]>()
  for (const event of events) {
    const dateKey = event.startDate
      ? format(parseISO(event.startDate), 'yyyy-MM-dd')
      : 'no-date'
    if (!map.has(dateKey)) map.set(dateKey, [])
    map.get(dateKey)!.push(event)
  }
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([dateKey, evts]) => {
      const d = dateKey !== 'no-date' ? parseISO(dateKey) : null
      return {
        dateKey,
        dateLabel: d ? format(d, 'MMM d, yyyy') : 'Date TBD',
        dayOfWeek: d ? format(d, 'EEEE') : '',
        events: evts.sort((a, b) => {
          const ta = a.startDate ? new Date(a.startDate).getTime() : 0
          const tb = b.startDate ? new Date(b.startDate).getTime() : 0
          return ta - tb
        }),
      }
    })
}

/** Compact showtime row for date-grouped view */
function ShowtimeRow({ event }: { event: TimedUIEvent }) {
  const formatPrice = useFormatPrice()
  const href = `/events/${event.slug || event.id}`
  const isSoldOut = event.status === 'SOLD_OUT'
  const timeStr = event.startDate
    ? format(parseISO(event.startDate), 'h:mm a')
    : ''
  // Filter first, then check (see EventContentSection): all-free or all-add-on
  // ticket lists otherwise reduced to Math.min() of nothing, "From $∞".
  const pricedTickets = event.ticketTypes.filter(t => t.allInPrice > 0 && !t.isAddon)
  const minPrice = pricedTickets.length > 0 ? Math.min(...pricedTickets.map(t => t.allInPrice)) : 0

  return (
    <Link
      href={href}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-surface-secondary active:bg-surface-secondary/80 transition-colors touch-manipulation group ${isSoldOut ? 'opacity-50' : ''}`}
    >
      {/* Time badge */}
      <span className={`inline-flex items-center justify-center w-[88px] flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold ${isSoldOut ? 'bg-surface-secondary text-text-tertiary' : 'bg-primary/10 text-[rgb(var(--color-primary))]'}`}>
        {timeStr || '--:--'}
      </span>

      {/* Event name — only show if it differs from the common group name */}
      <div className="flex-1 min-w-0">
        <span className="text-sm text-text-secondary truncate block">{event.title || event.name}</span>
      </div>

      {/* Status / Price */}
      <div className="flex-shrink-0 text-right">
        {isSoldOut ? (
          <span className="text-xs font-semibold text-error">Sold Out</span>
        ) : minPrice > 0 ? (
          <span className="text-sm font-semibold text-text-primary">
            {formatPrice(minPrice)}
          </span>
        ) : null}
      </div>

      {/* Chevron */}
      <svg className="w-4 h-4 text-text-tertiary flex-shrink-0 group-hover:text-text-secondary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  )
}

export default function LocationGroupView({ events, clickBehavior = 'expand', featuredSlugs = [] }: LocationGroupViewProps) {
  const groups = useMemo(() => {
    const map = new Map<string, LocationGroup>()

    for (const event of events) {
      // venueCity is "City, ST" formatted by the adapter — strip the
      // state for grouping + URL slugs so /locations/atlanta works
      // regardless of which state the venue's in. The full "City, ST"
      // would slugify to "atlanta-ga" and never match the city page.
      const rawCity = event.venueCity || event.venue?.city || ''
      const city = rawCity.split(',')[0].trim() || 'Unknown Location'
      let group = map.get(city)
      if (!group) {
        group = { city, venueNames: [], events: [] }
        map.set(city, group)
      }
      group.events.push(event)
      const venueName = event.venue?.name
      if (venueName && !group.venueNames.includes(venueName)) {
        group.venueNames.push(venueName)
      }
    }

    // Sort: featured cities first (in stored order), then alphabetical.
    // Featured slugs match against the slugified city name, so casing /
    // punctuation differences ("New York" vs "new-york") still match.
    const featuredIndex = new Map<string, number>(featuredSlugs.map((s, i) => [s, i]))
    const sorted = [...map.values()].sort((a, b) => {
      const ai = featuredIndex.get(citySlug(a.city)) ?? Infinity
      const bi = featuredIndex.get(citySlug(b.city)) ?? Infinity
      if (ai !== bi) return ai - bi
      return a.city.localeCompare(b.city)
    })
    for (const group of sorted) {
      group.events.sort((a, b) => {
        const da = a.startDate ? new Date(a.startDate).getTime() : 0
        const db = b.startDate ? new Date(b.startDate).getTime() : 0
        return da - db
      })
    }
    return sorted
  }, [events, featuredSlugs])

  // Auto-expand when 3 or fewer groups
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    if (groups.length <= 3) return new Set(groups.map(g => g.city))
    return new Set<string>()
  })

  const toggle = (city: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(city)) next.delete(city)
      else next.add(city)
      return next
    })
  }

  if (groups.length === 0) return null

  return (
    <div className="space-y-4">
      {groups.map(group => {
        const isOpen = expanded.has(group.city)
        const linkMode = clickBehavior === 'page'
        const isFeatured = featuredSlugs.includes(citySlug(group.city))

        // Detect if this location group has repeated events (same name = tour/residency)
        const uniqueNames = new Set(group.events.map(e => e.title || e.name))
        const isRepeatingSeries = uniqueNames.size <= 2 && group.events.length > 3
        const dateGroups = isOpen && isRepeatingSeries && !linkMode ? groupByDate(group.events) : null

        const headerInner = (
          <>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-[rgb(var(--color-primary))]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-text-primary truncate">{group.city}</h3>
                  {isFeatured && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-primary/10 text-[rgb(var(--color-primary))] rounded">
                      Featured
                    </span>
                  )}
                </div>
                {group.venueNames.length > 0 && (
                  <p className="text-xs text-text-tertiary truncate">{group.venueNames.join(', ')}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-xs font-medium text-text-secondary bg-surface-secondary px-2.5 py-1 rounded-full">
                {group.events.length} {group.events.length === 1 ? 'show' : 'shows'}
              </span>
              {linkMode ? (
                <svg className="w-5 h-5 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              ) : (
                <svg
                  className={`w-5 h-5 text-text-tertiary transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </div>
          </>
        )

        return (
          <div
            key={group.city}
            className="bg-surface border border-surface-border rounded-2xl overflow-hidden shadow-card"
          >
            {linkMode ? (
              <Link
                href={`/locations/${citySlug(group.city)}`}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-surface-secondary transition-colors"
              >
                {headerInner}
              </Link>
            ) : (
              <button
                onClick={() => toggle(group.city)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-surface-secondary transition-colors"
              >
                {headerInner}
              </button>
            )}

            {/* Expanded: date-grouped compact view for repeating series */}
            {isOpen && !linkMode && dateGroups && (
              <div className="border-t border-surface-border">
                {dateGroups.map((dg, i) => (
                  <div key={dg.dateKey}>
                    {i > 0 && <div className="border-t border-surface-border" />}
                    {/* Date sub-header */}
                    <div className="flex items-center gap-2 px-5 py-2.5 bg-surface-secondary/50">
                      <svg className="w-4 h-4 text-text-tertiary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm font-semibold text-text-primary">{dg.dayOfWeek}</span>
                      <span className="text-sm text-text-secondary">{dg.dateLabel}</span>
                      <span className="text-xs text-text-tertiary ml-auto">
                        {dg.events.length} {dg.events.length === 1 ? 'show' : 'shows'}
                      </span>
                    </div>
                    {/* Showtime rows */}
                    <div className="px-2 py-1">
                      {dg.events.map(event => (
                        <ShowtimeRow key={event.id} event={event} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Expanded: regular list for diverse events */}
            {isOpen && !linkMode && !dateGroups && (
              <div className="border-t border-surface-border px-4 py-3 space-y-2">
                {group.events.map(event => (
                  <EventListItem key={event.id} event={event} hideLocation />
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
